import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Toolbar,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LogoIcon } from "../atoms/LogoIcon";

interface NavbarProps {
  toggleTheme: () => void;
  mode: "light" | "dark";
}

export const Navbar: React.FC<NavbarProps> = () => {
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  return (
    <AppBar
      position="static"
      color="transparent"
      elevation={trigger ? 4 : 0}
      sx={{
        background: "transparent",
        boxShadow: trigger ? undefined : "none",
        transition: (theme) =>
          theme.transitions.create(["box-shadow"], {
            duration: theme.transitions.duration.short,
          }),
      }}
    >
      <Toolbar>
        <Box
          component={RouterLink}
          to="https://decisionnode.ai/"
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <LogoIcon
            src="/images/logo.svg"
            lightSrc="/images/logo.svg"
            width={"4em"}
            height={"4em"}
            alt="Decision Node Logo"
          />
          <Typography
            variant="h5"
            noWrap
            sx={{
              ml: 2,
              fontWeight: 700,
              marginLeft: 0,
              letterSpacing: 0.5,
              display: { xs: "none", md: "block" },
              textDecoration: "none",
              color: "hsla(216, 100%, 25%, 1.00)",
            }}
          >
            Decision Node
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isAuthenticated && (
              <div>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: "primary.main",
                    }}
                  >
                    {user?.email?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem
                    component={RouterLink}
                    to="/trees"
                    onClick={handleClose}
                  >
                    My Trees
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/settings"
                    onClick={handleClose}
                  >
                    Settings
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </div>
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};
